import { ChevronLeft, ChevronRight, Loading2, Plus, SparklesIcon } from 'assets/icons';
import Button from 'components/button';
import CompactEmptyState from 'components/EmptyState/CompactEmptyState';
import Drawer from 'components/crm/ui/Drawer';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import PageForm from 'components/pageForm/PageForm';
import AppointmentForm, { clockOf } from 'components/planning/AppointmentForm';
import PlacePanel from 'components/planning/PlacePanel';
import QuickCreate from 'components/planning/QuickCreate';
import WeekGrid from 'components/planning/WeekGrid';
import WeekPanel from 'components/planning/WeekPanel';
import PillTabs from 'components/tabs/PillTabs';
import PeopleSelect from 'components/tasks/PeopleSelect';
import UndoToast from 'components/toasts/UndoToast';
import { apiErrorText } from 'helper/apiErrorText';
import { coordsOf, distanceKm, sectorOf, travelMinutes } from 'helper/geo';
import { SUPER_ADMIN_ROLE } from 'helper/roles';
import { addDays, clockLabel, fromIsoDay, isoDay } from 'helper/timeLabels';
import useAppointmentPanel from 'hooks/useAppointmentPanel';
import useCrmContext from 'hooks/useCrmContext';
import usePlanningChanged, { useNotifyPlanningChanged } from 'hooks/usePlanningChanged';
import useTaskPanels from 'hooks/useTaskPanels';
import useTasksChanged from 'hooks/useTasksChanged';
import useText from 'hooks/useText';
import Usefetchdata from 'hooks/usefetchdata';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import crmApi from 'server/crm/crmApi';
import {
  FIRST_HOUR,
  LAST_HOUR,
  appointmentDays,
  appointmentLook,
  daysOf,
  firstFreeSlot,
  gridHours,
  isInvitation,
  isoWeek,
  longDay,
  mondayOf,
  partOfDay,
  responseOf,
  sameDay,
  spansDays,
  startOfDay,
  weekRange,
  weekStats,
  weekdayName,
} from './planningItems';

// Without a drag, a slot's length follows its type.
const MINUTES = { todo: 15, meeting: 30 };

// Where « Ajouter » puts a rendez-vous: the coming half hour of the working
// day, or 9:00 the next day once the day is over.
const nextAppointmentSlot = (now) => {
  const start = new Date(now);
  start.setSeconds(0, 0);
  start.setMinutes(start.getMinutes() < 30 ? 30 : 60);
  if (start.getHours() < FIRST_HOUR) start.setHours(FIRST_HOUR, 0, 0, 0);
  if (start.getHours() * 60 + start.getMinutes() + MINUTES.meeting > LAST_HOUR * 60) {
    start.setDate(start.getDate() + 1);
    start.setHours(9, 0, 0, 0);
  }
  return start;
};
const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

// The colleague whose planning an agency manager looks at, kept per user of the browser.
const personKey = (userId) => `planning-person:${userId}`;
// Codes meaning the kept colleague cannot be shown any more.
const PERSON_REFUSED = ['PLANNING_FORBIDDEN', 'INVALID_OWNER'];
const readStoredPerson = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
};
const storePerson = (key, value) => {
  try {
    if (value) localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable: the choice is not kept.
  }
};

/**
 * « Planning » of the CRM prototype: the week (or the day) of the sales rep,
 * their appointments by category, their tasks at a time and those of the
 * whole day, and beside it their week or the visits to place, which drag
 * into the grid or go in a proposed tour. A slot clicked or drawn creates a
 * task or a meeting. « Ajouter » opens the rendez-vous form on the next slot: a
 * rendez-vous is created here, never as a task. The data comes from the
 * api (server/crm/crmApi, contract in crmPlanningApi.js); `?date=` opens a day,
 * `?placer=1` the visits to place, `?nouveau=rendez-vous` a new rendez-vous
 * (the home page's « Ajouter un rendez-vous »).
 */
const PlanningPage = () => {
  const { text } = useText('planning');
  const { text: tasksText } = useText('tasks');
  const { text: fieldsText, language } = useText('appointmentFields');
  const visitTitle = (visit) => visit.company?.name ?? text(`visitReasons.${visit.reason}`);
  const failToast = (error) => {
    const message = apiErrorText(error, { language, text: tasksText, group: 'errors' });
    if (message) toast.error(message);
  };
  const { call, me, ready } = useCrmContext();
  const { userData } = Usefetchdata();
  // Only an agency manager looks at a colleague's planning (« Planning de »).
  const manager = userData?.role === SUPER_ADMIN_ROLE;
  const [team, setTeam] = useState([]);
  // `chosen` is undefined until the manager picks (or a refusal clears) a person.
  const [chosen, setChosen] = useState(undefined);
  const storedKey = me ? personKey(me.id) : null;
  const person = chosen !== undefined ? chosen : storedKey ? readStoredPerson(storedKey) : null;
  // Omitted for oneself: the api then answers the caller's own week.
  const ownerId = manager && person && person !== me?.id ? person : null;
  // The person whose week it is: their invitations, their answers, their tasks.
  const weekPerson = ownerId ?? me?.id ?? null;
  const { openAppointment } = useAppointmentPanel();
  const { openTask, openTaskForm } = useTaskPanels();
  const notify = useNotifyPlanningChanged();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(() => startOfDay(params.get('date') ? fromIsoDay(params.get('date')) : new Date()));
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [panel, setPanel] = useState(params.get('placer') ? 'place' : 'week');
  const [filter, setFilter] = useState('all');
  const [dragging, setDragging] = useState(null);
  const [far, setFar] = useState(null);
  const [flash, setFlash] = useState([]);
  // The slot clicked or drawn in the grid, waiting for its title.
  const [draft, setDraft] = useState(null);
  const closeDraft = useCallback(() => setDraft(null), []);
  // « Plus d'options » of a rendez-vous: the whole form, filled with its draft.
  const [appointmentDraft, setAppointmentDraft] = useState(null);
  const closeAppointmentForm = useCallback(() => setAppointmentDraft(null), []);

  // A phone shows one day: seven columns do not fit.
  const phone = useMediaQuery({ maxWidth: 768 });
  const view = phone ? 'day' : mode;

  const week = isoDay(mondayOf(anchor));
  const days = useMemo(() => daysOf(view, anchor), [view, anchor]);
  // Only the answer to the latest request lands: a slow week of a colleague
  // must not cover the one picked after it.
  const lastRequest = useRef(0);
  const load = useCallback(() => {
    const request = lastRequest.current + 1;
    lastRequest.current = request;
    // Plain local days: the api reads them in Martinique, whatever the browser's zone.
    const monday = fromIsoDay(week);
    return call(crmApi.getPlanning, { from: week, to: isoDay(addDays(monday, 7)), ...(ownerId ? { ownerId } : {}) }).then(
      (next) => {
        if (request !== lastRequest.current) return;
        setData(next);
        setFailed(false);
      },
      (error) => {
        if (request !== lastRequest.current) return;
        // A colleague the api refuses: back to oneself, which reloads once.
        if (ownerId && PERSON_REFUSED.includes(error?.response?.data?.errorCode)) {
          storePerson(storedKey, null);
          setChosen(null);
          toast.error(apiErrorText(error, { language, text: tasksText, group: 'errors' }));
          return;
        }
        setFailed(true);
      },
    );
  }, [call, week, ownerId, storedKey, tasksText, language]);
  useEffect(() => {
    if (ready) load();
  }, [load, ready]);
  usePlanningChanged(load);
  useTasksChanged(load);
  useEffect(() => {
    if (!ready || !manager) return;
    call(crmApi.getTeam).then(setTeam, () => setTeam([]));
  }, [call, manager, ready]);
  const colleagues = team.filter((member) => member.id !== me?.id);
  const pickPerson = (id) => {
    const next = id && id !== me?.id ? id : null;
    storePerson(storedKey, next);
    setChosen(next);
    setData(null);
  };
  // « Ajouter », or the home page's link: a rendez-vous on the next slot, as
  // if it had been clicked in the grid.
  const addAppointment = useCallback(() => {
    const start = nextAppointmentSlot(new Date());
    setAnchor(startOfDay(start));
    setDraft(null);
    const end = new Date(start.getTime() + MINUTES.meeting * 60000);
    // Same shape as the « Plus d'options » conversion of QuickCreate. In a
    // colleague's week, « Agenda de » proposes the colleague.
    setAppointmentDraft({
      title: '',
      ownerId,
      allDay: false,
      startDay: isoDay(start),
      endDay: isoDay(end),
      // HH:MM: a time input empties « 9:00 ».
      start: clockOf(start),
      end: clockOf(end),
      description: '',
    });
  }, [ownerId]);
  // The address opened a day, the visits to place or a new rendez-vous: it
  // forgets them now.
  useEffect(() => {
    if (!params.get('placer') && !params.get('date') && !params.get('nouveau')) return;
    if (params.get('nouveau') === 'rendez-vous') addAppointment();
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('placer');
        next.delete('date');
        next.delete('nouveau');
        return next;
      },
      { replace: true },
    );
  }, [params, setParams, addAppointment]);

  // An internal meeting shows its own title, the others their company; a
  // masked one (private, not the caller's) only says the slot is taken.
  const titleOf = useCallback(
    (event) => {
      if (event.masked) return text('busy');
      return (event.category === 'interne' ? event.title : event.company?.name || event.title) || text('untitled');
    },
    [text],
  );
  // How an appointment looks in the week: its colour, its availability, the
  // answer of the person of the week, and whether they are only invited.
  const lookOf = useCallback(
    (event) => ({
      look: appointmentLook(event, responseOf(event, weekPerson, !ownerId)),
      invitation: isInvitation(event, weekPerson),
      masked: Boolean(event.masked),
    }),
    [ownerId, weekPerson],
  );
  // The appointments with hours within a day; the others go in the row of the day.
  const gridEvents = useMemo(() => (data?.events ?? []).filter((event) => !spansDays(event)), [data]);
  // The hours of the whole week, so that the day view keeps them.
  const hours = useMemo(() => {
    const monday = fromIsoDay(week);
    const from = startOfDay(monday);
    const to = addDays(from, 7);
    return gridHours(gridEvents.filter((event) => new Date(event.startsAt) >= from && new Date(event.startsAt) < to));
  }, [gridEvents, week]);

  const items = useMemo(() => {
    if (!data) return [];
    const at = new Date();
    const from = days[0];
    const to = addDays(days[days.length - 1], 1);
    const events = gridEvents.filter((event) => new Date(event.startsAt) >= from && new Date(event.startsAt) < to);
    const out = events.map((event) => {
      const start = new Date(event.startsAt);
      const end = new Date(event.endsAt);
      // The road to the next appointment of the day, under this one.
      const next = events
        .filter((other) => sameDay(other.startsAt, start) && new Date(other.startsAt) >= end && other.travelMinutes)
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))[0];
      return {
        id: event.id,
        title: titleOf(event),
        titleTime: clockLabel(start),
        subtitle: event.category === 'interne' ? '' : event.company?.city || event.location,
        start,
        end,
        category: event.category,
        past: end < at,
        done: event.status === 'done',
        travelAfter: next && Math.round((new Date(next.startsAt) - end) / 60000) <= next.travelMinutes + 15 ? next.travelMinutes : null,
        flash: flash.includes(event.id),
        google: event.source === 'google',
        ...lookOf(event),
        onClick: () => openAppointment(event.id),
      };
    });
    // The tasks at a time, on their length; they open in their panel.
    data.tasks.forEach((task) => {
      if (!task.dueTime) return;
      const start = new Date(`${task.dueOn}T${task.dueTime.slice(0, 5)}`);
      const end = new Date(start.getTime() + (task.durationMin ?? 15) * 60000);
      if (start < from || start >= to) return;
      out.push({
        id: `task-${task.id}`,
        title: task.title,
        titleTime: clockLabel(start),
        subtitle: task.company?.name ?? '',
        start,
        end,
        category: 'interne',
        plain: true,
        past: Boolean(task.doneAt) || end < at,
        flash: flash.includes(`task-${task.id}`),
        onClick: () => openTask(task.id),
      });
    });
    if (far) {
      out.push({
        id: 'far',
        title: visitTitle(far.visit),
        subtitle: far.visit.company?.city ?? '',
        start: far.start,
        end: new Date(far.start.getTime() + (far.visit.durationMin || 45) * 60000),
        category: far.visit.category,
        danger: true,
      });
    }
    return out;
  }, [data, days, far, flash, gridEvents, lookOf, openAppointment, openTask, titleOf]);

  // Under the date of each day: the appointments of the whole day or of
  // several days, then the tasks without a time.
  const allDay = useMemo(() => {
    const out = {};
    const add = (day, entry) => {
      out[day] = [...(out[day] ?? []), entry];
    };
    (data?.events ?? [])
      .filter(spansDays)
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
      .forEach((event) => {
        appointmentDays(event).forEach((day, index) =>
          add(day, {
            id: `appointment-${event.id}`,
            title: titleOf(event),
            // Several days with hours: the start on the first one.
            time: !event.allDay && index === 0 ? clockLabel(new Date(event.startsAt)) : '',
            category: event.category,
            google: event.source === 'google',
            appointment: true,
            ...lookOf(event),
            onClick: () => openAppointment(event.id),
          }),
        );
      });
    (data?.tasks ?? []).forEach((task) => {
      if (task.dueTime) return;
      add(task.dueOn, { id: task.id, title: task.title, done: Boolean(task.doneAt), onClick: () => openTask(task.id) });
    });
    return out;
  }, [data, lookOf, openAppointment, openTask, titleOf]);

  const undoable = (message, ids) =>
    toast.success(
      <UndoToast
        message={message}
        undoLabel={text('undo')}
        onUndo={async () => {
          try {
            // giveVisitBack: unlike a deletion from the panel, the undo puts the visit back to place.
            await Promise.all(ids.map((id) => call(crmApi.removeAppointment, { id, giveVisitBack: true })));
            load();
            notify();
          } catch (error) {
            failToast(error);
            // Some may be removed already: the week shows what is left.
            load();
          }
        }}
      />,
    );

  const placeOne = async (visit, start) => {
    try {
      const placed = await call(crmApi.placeVisit, { visitId: visit.id, startsAt: start.toISOString() });
      setFlash([placed.id]);
      undoable(text('placedOne', { company: visitTitle(visit), day: weekdayName(start), time: clockLabel(start) }), [placed.id]);
      load();
      notify();
    } catch (error) {
      failToast(error);
    }
  };

  const placeTour = async (tour) => {
    // The start of each stop follows the visit and the road before it.
    let start = new Date(tour.start);
    const stops = tour.ordered.map((visit, index) => {
      if (index) start = new Date(start.getTime() + tour.legs[index] * 60000);
      const stop = { visitId: visit.id, startsAt: start.toISOString(), travelMinutes: tour.legs[index] || null };
      start = new Date(start.getTime() + (visit.durationMin || 45) * 60000);
      return stop;
    });
    try {
      // All or none: the api places the whole tour in one transaction.
      const placed = await call(crmApi.placeVisits, { stops });
      const ids = (placed?.appointments ?? []).map((appointment) => appointment.id);
      const road = tour.legs.reduce((sum, leg) => sum + leg, 0);
      setAnchor(startOfDay(tour.start));
      setFlash(ids);
      const where = text(partOfDay(tour.start), { day: weekdayName(tour.start) });
      undoable(
        ids.length > 1 ? text('placedTour', { count: ids.length, where, road }) : text('placedTourOne', { where }),
        ids,
      );
      load();
      notify();
    } catch (error) {
      failToast(error);
      // A visit placed meanwhile (409): the week and its visits to place are read again.
      load();
    }
  };

  // A visit dropped far from the other appointments of its day: we say so,
  // and propose a day on the road of a tour of its sector.
  const onDrop = (day, start) => {
    const visit = dragging;
    setDragging(null);
    if (!visit) return;
    const here = coordsOf(visit.company);
    let nearest = null;
    data.events
      .filter((event) => sameDay(event.startsAt, day) && coordsOf(event.company) && event.company?.id !== visit.company?.id)
      .forEach((event) => {
        const km = distanceKm(here, coordsOf(event.company));
        if (km !== null && (!nearest || km < nearest.km)) nearest = { event, km };
      });
    if (!nearest || nearest.km <= 20) {
      placeOne(visit, start);
      return;
    }
    const sector = sectorOf(visit.company?.city);
    let suggestion = null;
    for (let index = 0; index < 12 && !suggestion; index += 1) {
      const other = addDays(startOfDay(new Date()), index);
      if (other.getDay() !== 0 && other.getDay() !== 6 && !sameDay(other, day)) {
        const near = data.events.find((event) => sameDay(event.startsAt, other) && sectorOf(event.company?.city) === sector);
        const slot = near && firstFreeSlot(other, data.events, visit.durationMin || 45);
        if (slot && slot > new Date()) suggestion = { start: slot, near };
      }
    }
    setFar({ visit, day, start, nearest, suggestion });
  };

  const monday = mondayOf(anchor);
  const label = view === 'week' ? `${weekRange(monday)} (S${isoWeek(monday)})` : longDay(anchor);
  const move = (step) => setAnchor(addDays(anchor, view === 'week' ? 7 * step : step));

  // A task or a meeting created here: the planning goes to its week and lights it up.
  const created = (saved) => {
    const day = startOfDay(saved.start);
    if (isoDay(mondayOf(day)) !== week) setAnchor(day);
    else load();
    setFlash([saved.kind === 'appointment' ? saved.id : `task-${saved.id}`]);
  };
  // The whole form saved: the planning (and the home page) read it again.
  const appointmentCreated = (appointment) => {
    setAppointmentDraft(null);
    notify();
    if (!appointment?.startsAt) return;
    const day = startOfDay(new Date(appointment.startsAt));
    if (isoDay(mondayOf(day)) !== week) setAnchor(day);
    setFlash([appointment.id]);
  };
  const onDraft = (slot) =>
    setDraft({ type: 'todo', title: '', note: '', ...slot, minutes: slot.minutes ?? MINUTES.todo, fixed: Boolean(slot.minutes), ownerId, assigneeId: ownerId });
  const changeDraft = (patch) =>
    setDraft((current) => ({ ...current, ...patch, ...(patch.type && !current.fixed ? { minutes: MINUTES[patch.type] } : {}) }));
  // « Plus d'options »: the whole form of a rendez-vous, or of a task.
  const moreOptions = (appointment) => {
    if (draft.type === 'meeting') {
      setAppointmentDraft(appointment);
      setDraft(null);
      return;
    }
    openTaskForm({
      defaults: {
        note: [draft.title.trim(), (draft.note ?? '').trim()].filter(Boolean).join('\n\n'),
        dueOn: isoDay(draft.start),
        time: draft.allDay ? '' : clockLabel(draft.start),
        ...(draft.fixed ? { duration: draft.minutes } : {}),
        // In a colleague's week, the task is theirs.
        ...(draft.assigneeId ? { assigneeId: draft.assigneeId } : {}),
      },
    });
    setDraft(null);
  };

  const stats = data && weekStats(data, monday, weekPerson);
  const showsToday = days.some((day) => sameDay(day, new Date()));

  return (
    <PageForm>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-[70px] shrink-0 flex-wrap items-center gap-3 px-4 py-3 lg:px-8">
          <div className="flex items-center gap-1">
            <IconTooltip label={text('previous')}>
              <Button color="SECONDARY" size="ICON" className="h-10 w-10 border-gray-200" aria-label={text('previous')} onClick={() => move(-1)}>
                <ChevronLeft wh={18} color="#374151" />
              </Button>
            </IconTooltip>
            <IconTooltip label={text('next')}>
              <Button color="SECONDARY" size="ICON" className="h-10 w-10 border-gray-200" aria-label={text('next')} onClick={() => move(1)}>
                <ChevronRight wh={18} color="#374151" />
              </Button>
            </IconTooltip>
          </div>
          <h2 className="ml-1 ft-b font-semibold text-gray-900">{label}</h2>
          {/* Useless when the view already shows today. */}
          {!showsToday && (
            <Button color="SECONDARY" size="SM" className="ml-2" onClick={() => setAnchor(startOfDay(new Date()))}>
              {text('today')}
            </Button>
          )}
          <span className="flex-1" />
          {/* The controls on the right sit closer together, as in the tasks' bar. */}
          <div className="flex items-center gap-2">
            {/* Whose week: the people picker of the tasks, one person at a time. */}
            {manager && colleagues.length > 0 && (
              <PeopleSelect
                single
                ariaLabel={text('planningOf')}
                team={team.some((member) => member.id === me?.id) ? team : [me, ...team]}
                meId={me?.id}
                value={ownerId ?? me?.id}
                onChange={pickPerson}
              />
            )}
            {!phone && (
              <PillTabs
                activeTab={mode}
                onChange={setMode}
                tabs={[
                  { id: 'day', label: text('day') },
                  { id: 'week', label: text('week') },
                ]}
              />
            )}
            <Button color="PRIMARY" size="SM" className="h-10" onClick={addAppointment}>
              <Plus wh={18} color="currentColor" />
              <span>{text('add')}</span>
            </Button>
          </div>
        </div>
        <main className="relative flex min-h-0 flex-1 gap-6 border-t border-gray-100 px-4 pb-6 pt-5 lg:px-8">
          {failed && <CompactEmptyState title={text('loadError')} action={{ label: text('retry'), onClick: load }} />}
          {!failed && !data && (
            <div className="flex flex-1 items-center justify-center" aria-busy="true">
              <span className="animate-spin">
                <Loading2 wh={24} color="#FF9600" />
              </span>
            </div>
          )}
          {!failed && data && (
            <>
              <WeekGrid
                days={days}
                items={items}
                dragging={
                  dragging && {
                    minutes: dragging.durationMin || 45,
                    title: visitTitle(dragging),
                    city: dragging.company?.city ?? '',
                  }
                }
                onDrop={onDrop}
                highlight={far?.day}
                allDay={allDay}
                firstHour={hours.first}
                lastHour={hours.last}
                draft={draft}
                onDraft={onDraft}
                popover={
                  draft && (
                    <QuickCreate
                      draft={draft}
                      onChange={changeDraft}
                      onClose={closeDraft}
                      onMore={moreOptions}
                      onSaved={(saved) => {
                        setDraft(null);
                        created(saved);
                      }}
                    />
                  )
                }
              />
              <div className="hidden lg:flex">
                {panel === 'week' ? (
                  <WeekPanel
                    label={`S${isoWeek(monday)} · ${weekRange(monday)}`}
                    ownerName={ownerId ? team.find((member) => member.id === ownerId)?.firstname : null}
                    toPlace={data.visits.length + (data.toPlace?.length ?? 0)}
                    stats={stats}
                    onPlace={() => setPanel('place')}
                  />
                ) : (
                  <PlacePanel
                    visits={data.visits}
                    appointments={data.toPlace ?? []}
                    onOpenAppointment={(appointment) => openAppointment(appointment.id)}
                    events={data.events}
                    filter={filter}
                    onFilter={setFilter}
                    onBack={() => setPanel('week')}
                    onDragStart={(visit) => {
                      setFar(null);
                      setDragging(visit);
                    }}
                    onDragEnd={() => setTimeout(() => setDragging(null), 50)}
                    draggingId={dragging?.id}
                    onPlaceTour={placeTour}
                  />
                )}
              </div>
            </>
          )}
          {far && (
            <div className="absolute left-1/3 top-1/3 z-30 w-[300px] rounded-2xl border border-gray-100 bg-white p-4 shadow-lg">
              <p className="text-sm font-semibold text-gray-900">{text('farTitle')}</p>
              <p className="mt-2 text-sm leading-5 text-gray-700">
                {text(new Date(far.nearest.event.startsAt) > far.start ? 'farTextBefore' : 'farTextAfter', {
                  city: far.visit.company?.city ?? '',
                  km: Math.round(far.nearest.km),
                  from: far.nearest.event.company?.city ?? '',
                  minutes: travelMinutes(far.nearest.km),
                  company: far.nearest.event.company?.name ?? '',
                  time: clockLabel(new Date(far.nearest.event.startsAt)),
                })}
              </p>
              {far.suggestion && (
                <div className="mt-3 rounded-xl bg-gray-100 p-3">
                  <p className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <span className="flex text-gray-500">
                      <SparklesIcon wh={14} color="currentColor" />
                    </span>
                    {text('farSuggestion', { day: capitalize(weekdayName(far.suggestion.start)), time: clockLabel(far.suggestion.start) })}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {text(partOfDay(new Date(far.suggestion.near.startsAt)) === 'morning' ? 'farOnTourMorning' : 'farOnTourAfternoon', {
                      sector: sectorOf(far.suggestion.near.company?.city),
                    })}
                  </p>
                </div>
              )}
              <div className="mt-3 flex gap-2">
                {far.suggestion && (
                  <Button
                    color="PRIMARY"
                    size="SM"
                    onClick={() => {
                      const kept = far;
                      setFar(null);
                      placeOne(kept.visit, kept.suggestion.start);
                      setAnchor(startOfDay(kept.suggestion.start));
                    }}
                  >
                    {text('placeOn', { day: weekdayName(far.suggestion.start) })}
                  </Button>
                )}
                <Button
                  color="SECONDARY"
                  size="SM"
                  onClick={() => {
                    const kept = far;
                    setFar(null);
                    placeOne(kept.visit, kept.start);
                  }}
                >
                  {text('keepOn', { day: weekdayName(far.start) })}
                </Button>
              </div>
              <button type="button" onClick={() => setFar(null)} className="mt-2 text-xs text-gray-600 hover:underline">
                {text('cancel')}
              </button>
            </div>
          )}
        </main>
      </div>
      <Drawer
        open={Boolean(appointmentDraft)}
        onClose={closeAppointmentForm}
        title={fieldsText('createTitle')}
        description="Planifiez un rendez-vous dans votre agenda."
      >
        {appointmentDraft && (
          <AppointmentForm
            mode="create"
            chooseRecord
            initial={appointmentDraft}
            calendarSynced={Boolean(data?.calendarSynced)}
            onSaved={appointmentCreated}
            onCancel={closeAppointmentForm}
          />
        )}
      </Drawer>
    </PageForm>
  );
};

export default PlanningPage;
